import { TILE_SIZES } from '@mixtiles/web-backend-shared'
import { PHOTO_BOOK_URL, PHOTO_STYLER_URL } from 'config/config'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import type { CartItem } from 'stores/cartStore/CartItem'
import { getOrderIndexByCartItemId } from 'stores/cartStore/cartOrderKey'
import type { GroupPricingProduct } from './groupCheckoutPricing.types'
import type { CartItemType } from './components/ProductSummarySegment/ProductSummarySegment.types'

const PRODUCT_TYPE_TO_CART_TYPE: Record<string, CartItemType> = {
  [PRODUCT_TYPES.CLASSIC]: 'TILES',
  [PRODUCT_TYPES.PHOTO_BOOK]: 'PHOTO_BOOK',
}

export function getCartItemType(productType: string): CartItemType {
  const cartItemType = PRODUCT_TYPE_TO_CART_TYPE[productType]
  if (!cartItemType) {
    throw new Error(`Unexpected productType: ${productType}`)
  }
  return cartItemType
}

// Null marks an item that has no editor to send the user to.
export function getEditRoute(item: CartItem): string | null {
  switch (getCartItemType(item.productType)) {
    case 'TILES':
      return PHOTO_STYLER_URL
    case 'PHOTO_BOOK':
      return `${PHOTO_BOOK_URL}/${item.id}`
    default:
      return null
  }
}

export function getPricingItemsForCartItem(
  item: CartItem,
  products: GroupPricingProduct[],
  cartItems: CartItem[]
): GroupPricingProduct[] {
  const productsForType = products.filter(
    (product) => product.productType === item.productType
  )

  switch (item.productType) {
    case PRODUCT_TYPES.CLASSIC: {
      const sizes = new Set<string>(Object.keys(item.metadata.tileSizes))

      // metadata.tileSizes does not include centerpieces, we include them manually since pricing products include them.
      if (item.metadata.centerpiecesCount > 0) {
        sizes.add(TILE_SIZES.RECTANGLE_25X8)
      }

      return productsForType.filter(
        (p) =>
          p.pricingProduct?.tileSize && sizes.has(p.pricingProduct.tileSize)
      )
    }
    case PRODUCT_TYPES.PHOTO_BOOK: {
      const orderIndex = getOrderIndexByCartItemId(cartItems).get(item.id)
      return productsForType.filter(
        (product) => product.orderIndex === orderIndex
      )
    }
    default:
      return []
  }
}

export function sumPricingProducts(products: GroupPricingProduct[]): {
  originalPrice: number
  discountedPrice: number
} {
  return {
    originalPrice: products.reduce(
      (sum, p) => sum + (p.priceBeforeDiscount ?? 0),
      0
    ),
    discountedPrice: products.reduce(
      (sum, p) => sum + (p.priceAfterDiscount ?? 0),
      0
    ),
  }
}
